import { Link } from "@tanstack/react-router";
import { Logo } from "@/components/ui/logo";
import { buttonVariants } from "@/components/ui/button-util";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  component: Terms,
});

function Terms() {
  return (
    <div className="relative min-h-screen w-full bg-card">
      {/* Navigation */}
      <div className="sticky top-0 z-50 border-b border-border/50 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/75">
        <div className="mx-auto flex w-full max-w-screen-lg items-center justify-between p-4">
          <Link to="/" className="flex h-10 items-center gap-1">
            <Logo />
          </Link>
          <Link
            to="/"
            className={buttonVariants({
              variant: "ghost",
              size: "sm",
            })}
          >
            Back to Home
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="prose prose-lg dark:prose-invert mx-auto">
          <h1 className="text-4xl font-bold tracking-tight">
            Terms of Service
          </h1>

          <div className="mt-8 space-y-12">
            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                1. Acceptance of Terms
              </h2>
              <p className="mt-4 text-muted-foreground">
                By accessing and using CreatorGrid's services, you agree to be
                bound by these Terms of Service and all applicable laws and
                regulations. If you do not agree with any of these terms, you
                are prohibited from using or accessing our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                2. Description of Service
              </h2>
              <p className="mt-4 text-muted-foreground">
                CreatorGrid provides a platform connecting creators and brands
                for content creation and collaboration opportunities. Our
                services include but are not limited to creator residency
                programs, brand partnerships, and content creation management
                tools.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                3. User Accounts
              </h2>
              <p className="mt-4 text-muted-foreground">
                To access certain features of our platform, you must register
                for an account. You agree to provide accurate, current, and
                complete information during registration and to update such
                information to keep it accurate, current, and complete.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                4. Content and Conduct
              </h2>
              <p className="mt-4 text-muted-foreground">
                Users are responsible for all content they post, upload, or
                otherwise make available through our services. You agree not to
                use our services for any unlawful purpose or in violation of any
                third-party rights.
              </p>
              <p className="mt-4 text-muted-foreground">
                No paid boost or ads allowed, views must be organic. Any form of
                fake views, bot activity, or misleading content will disqualify
                your entry.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                5. CreatorGrid Disclosure Guidelines
              </h2>
              <p className="mt-4 text-muted-foreground">
                Given that Flexible Brand Deals via CreatorGrid are paid
                partnerships, it's important to disclose this appropriately in
                your content.
              </p>
              <ul className="mt-4 space-y-2 text-muted-foreground">
                <li>
                  • Use #ad, #sponsored, or "Paid partnership with [Brand Name]
                  via CreatorGrid" in your caption or video description.
                </li>
                <li>
                  • Make sure the disclosure is easy to see—not buried in
                  hashtags or hidden at the end.
                </li>
                <li>
                  • For video, say or show that it's a paid partnership with the
                  brand through CreatorGrid.
                </li>
              </ul>
              <p className="mt-4 text-muted-foreground">
                Disclosures are required by law in most countries, and help keep
                you, brands, and CreatorGrid protected.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                6. Intellectual Property
              </h2>
              <p className="mt-4 text-muted-foreground">
                By submitting content on CreatorGrid, you transfer all
                intellectual property (IP) rights to the company sponsoring the
                campaign. The sponsoring company can reuse or repackage that
                content as they see fit in any type of future campaigns,
                including ads.
              </p>
              <p className="mt-4 text-muted-foreground">
                By using our services, you grant CreatorGrid a non-exclusive
                license to use, store, and display your content for the purpose
                of providing and improving our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                7. Payment Terms
              </h2>
              <p className="mt-4 text-muted-foreground">
                For paid services, you agree to pay all fees according to the
                pricing established in your partnership or program agreements.
                All payments are non-refundable unless otherwise specified in
                writing.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                8. Termination
              </h2>
              <p className="mt-4 text-muted-foreground">
                We reserve the right to terminate or suspend access to our
                services immediately, without prior notice or liability, for any
                reason whatsoever, including without limitation if you breach
                the Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                9. Limitation of Liability
              </h2>
              <p className="mt-4 text-muted-foreground">
                CreatorGrid shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages resulting from your
                use or inability to use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                10. Changes to Terms
              </h2>
              <p className="mt-4 text-muted-foreground">
                We reserve the right to modify or replace these Terms at any
                time. We will provide notice of any changes by posting the new
                Terms on our platform. Your continued use of the service after
                such changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold tracking-tight">
                11. Contact Information
              </h2>
              <p className="mt-4 text-muted-foreground">
                For any questions about these Terms, please contact us at{" "}
                <a
                  href="mailto:creators@creatorgrid.io"
                  className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
                >
                  creators@creatorgrid.io
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
